import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createToggleRow, createSliderRow, safeStorage } from '@learnforge/demo-kit';
import {
  type SequenceSpec,
  type ArithmeticSpec,
  nthTerm,
  partialSum,
  closedFormExpr,
  partialSumClosedForm,
  type Expr,
  type Fraction,
  format,
  Fraction as FractionClass,
} from '../algebra';
import type { AlgebraContext, AlgebraKey } from '../types';
import { createStaircase, fitStaircaseDomains } from '../render/staircase';
import {
  createCartesianViewport,
  createFormulaLegend,
  createSvgEl,
  type CartesianDomain,
} from '@learnforge/demo-render';
import { mountActionBar } from '../action-bar';
import { SEQUENCE_PRESETS } from '../data/sequence-presets';

// ============================================================================
// Helper Interfaces and Pure Functions (exported and tested)
// ============================================================================

export interface GaussFrame {
  index: number;
  topValue: string;
  bottomValue: string;
  pairSum: string;
}

/**
 * Generate Gauss pairing frames for an arithmetic sequence.
 * Pairs term i (1-based) with term (steps+1-i); pairSum is constant.
 */
export function gaussPairingFrames(spec: ArithmeticSpec, steps: number): GaussFrame[] {
  const frames: GaussFrame[] = [];
  for (let i = 1; i <= steps; i += 1) {
    const topVal = nthTerm(spec, i);
    const bottomVal = nthTerm(spec, steps + 1 - i);
    const pairSum = topVal.add(bottomVal);
    frames.push({
      index: i,
      topValue: topVal.toString(),
      bottomValue: bottomVal.toString(),
      pairSum: pairSum.toString(),
    });
  }
  return frames;
}

export interface TelescopePair {
  left: string;
  right: string;
}

/**
 * Generate telescoping fade sequence for partial-fraction expansion.
 * Returns ordered list of cancelling pairs.
 */
export function telescopingFadeSequence(
  decomp: { partial: Expr; collapsed: Expr } | undefined,
  terms: number,
): TelescopePair[] {
  // decomp structure: { partial: 1/k - 1/(k+1), collapsed: 1 - 1/(n+1) }
  // Currently this function generates pairs for the standard 1/(k(k+1)) case
  void decomp; // Mark as intentionally unused for now

  const pairs: TelescopePair[] = [];

  // For the standard 1/(k(k+1)) decomposition: 1/k - 1/(k+1)
  // Build pairs at each k from 1 to terms
  for (let k = 1; k <= terms; k += 1) {
    const kVal = k;
    const kNext = k + 1;

    // Format the two terms: 1/k and 1/(k+1)
    const leftStr = `1/${kVal}`;
    const rightStr = `1/${kNext}`;

    pairs.push({
      left: leftStr,
      right: rightStr,
    });
  }

  return pairs;
}

// ============================================================================
// State
// ============================================================================

interface State {
  presetId: string;
  advancedMode: boolean;
  spec: SequenceSpec;
  highlightedN: number | null;
  showSigmaStack: boolean;
  showClosedForm: boolean;
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): CustomExperimentSpec<State, AlgebraKey, AlgebraContext> {
  return {
    labelKey: 'experiment.sequences-staircase.label',
    demoId: 'algebra',
    advancedKey: 'sequences-staircase',

    initialState(ctx): State {
      const defaultPreset = SEQUENCE_PRESETS[0]!;
      return {
        presetId: defaultPreset.id,
        advancedMode: ctx.advanced,
        spec: { ...defaultPreset.toSpec() },
        highlightedN: null,
        showSigmaStack: false,
        showClosedForm: true,
      };
    },

    applyPreset(state, presetId): State {
      const preset = SEQUENCE_PRESETS.find((p) => p.id === presetId);
      if (!preset) return state;
      return {
        ...state,
        presetId: preset.id,
        spec: preset.toSpec(),
      };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage ──
      const svg = createSvgEl('svg', { viewBox: '0 0 800 380', class: 'alg-staircase-svg' });
      host.appendChild(svg);
      const legend = createFormulaLegend(host);
      legend.element.classList.add('alg-formula-legend--sequence');
      let resetView = (): void => {};

      // We need a reference initial spec for creating the staircase.
      // We'll read it from the current state in the first update() call, but
      // createStaircase needs initial options — use a placeholder that update()
      // will immediately reconcile.
      const initPreset = SEQUENCE_PRESETS[0]!;
      const initSpec = initPreset.toSpec();
      const staircase = createStaircase(svg, {
        mode:
          initSpec.kind === 'arithmetic' || initSpec.kind === 'geometric'
            ? initSpec.kind
            : 'arithmetic',
        a1: initSpec.a1,
        ratioOrDiff:
          initSpec.kind === 'arithmetic'
            ? initSpec.d
            : initSpec.kind === 'geometric'
              ? initSpec.r
              : initSpec.d,
        steps: 8,
        showSigmaStack: false,
        width: 760,
        height: 290,
      });
      staircase.group.setAttribute('transform', 'translate(20, 70)');
      let visibleDomains: CartesianDomain = fitStaircaseDomains({
        mode: 'arithmetic',
        a1: initSpec.a1,
        ratioOrDiff:
          initSpec.kind === 'arithmetic'
            ? initSpec.d
            : initSpec.kind === 'geometric'
              ? initSpec.r
              : initSpec.c,
        steps: 8,
        showSigmaStack: false,
      });
      const viewport = createCartesianViewport(svg, {
        base: visibleDomains,
        plotRect: { x: 20, y: 70, width: 760, height: 290 },
        touchActionTarget: staircase.group,
        onChange(next) {
          visibleDomains = next;
          staircase.update(visibleDomains);
        },
      });
      resetView = () => viewport.reset();
      let lastDomainKey: string | null = null;

      // ── DOM: Formula group (in-stage, rebuilt in update()) ──
      const formulaGroup = createSvgEl('g');
      svg.appendChild(formulaGroup);

      // ── DOM: Panel ──

      // Sequence kind selector
      const kindRow = document.createElement('div');
      kindRow.className = 'alg-panel-row alg-dropdown-row';
      const kindLabel = document.createElement('label');
      kindLabel.textContent = 'Sequence kind:';
      const kindSelect = document.createElement('select');
      kindSelect.className = 'alg-select';

      const arithOption = document.createElement('option');
      arithOption.value = 'arithmetic';
      arithOption.textContent = ctx.i18n.t('class.arithmetic');
      kindSelect.appendChild(arithOption);

      const geomOption = document.createElement('option');
      geomOption.value = 'geometric';
      geomOption.textContent = ctx.i18n.t('class.geometric');
      kindSelect.appendChild(geomOption);

      // Recurrence option — added/removed by update() based on advancedMode
      let recurOption: HTMLOptionElement | null = null;

      kindSelect.addEventListener('change', () => {
        const newKind = kindSelect.value as 'arithmetic' | 'geometric' | 'recurrence';
        controls.setState((s) => {
          const currentSpec = s.spec;
          let nextSpec: SequenceSpec;
          if (newKind === 'arithmetic') {
            nextSpec = {
              kind: 'arithmetic',
              a1: currentSpec.a1,
              d:
                currentSpec.kind === 'arithmetic'
                  ? currentSpec.d
                  : currentSpec.kind === 'geometric'
                    ? currentSpec.r
                    : currentSpec.d,
            };
          } else if (newKind === 'geometric') {
            nextSpec = {
              kind: 'geometric',
              a1: currentSpec.a1,
              r:
                currentSpec.kind === 'geometric'
                  ? currentSpec.r
                  : currentSpec.kind === 'arithmetic'
                    ? currentSpec.d
                    : currentSpec.d,
            };
          } else {
            nextSpec = {
              kind: 'recurrence',
              a1: currentSpec.a1,
              c:
                currentSpec.kind === 'recurrence'
                  ? currentSpec.c
                  : currentSpec.kind === 'geometric'
                    ? currentSpec.r
                    : currentSpec.d,
              d:
                currentSpec.kind === 'recurrence'
                  ? currentSpec.d
                  : currentSpec.kind === 'geometric'
                    ? currentSpec.r
                    : currentSpec.d,
            };
          }
          return { ...s, spec: nextSpec };
        });
      });

      kindRow.appendChild(kindLabel);
      kindRow.appendChild(kindSelect);
      ctx.panel.appendChild(kindRow);

      const a1Slider = createSliderRow({
        id: 'seq-a1',
        label: 'a₁:',
        i18n: ctx.i18n,
        min: -10,
        max: 10,
        step: 0.5,
        value: 1,
        onChange: (v) => {
          controls.setState((s) => ({ ...s, spec: { ...s.spec, a1: FractionClass.from(v) } }));
        },
      });
      ctx.panel.appendChild(a1Slider.element);

      const drSlider = createSliderRow({
        id: 'seq-dr',
        label: 'd:',
        i18n: ctx.i18n,
        min: -10,
        max: 10,
        step: 0.1,
        value: 1,
        onChange: (v) => {
          controls.setState((s) => {
            const newVal = FractionClass.from(v);
            const currentSpec = s.spec;
            let nextSpec: SequenceSpec;
            if (currentSpec.kind === 'arithmetic') {
              nextSpec = { ...currentSpec, d: newVal };
            } else if (currentSpec.kind === 'geometric') {
              nextSpec = { ...currentSpec, r: newVal };
            } else {
              nextSpec = { ...currentSpec, c: newVal };
            }
            return { ...s, spec: nextSpec };
          });
        },
      });
      ctx.panel.appendChild(drSlider.element);
      const drLabelEl = drSlider.element.querySelector('.dim-label') as HTMLLabelElement;

      // n input for term lookup
      const nRow = document.createElement('div');
      nRow.className = 'alg-panel-row';
      const nLabel = document.createElement('label');
      nLabel.textContent = 'Highlight term n:';
      const nInput = document.createElement('input');
      nInput.type = 'number';
      nInput.className = 'alg-text-input';
      nInput.min = '1';
      nInput.placeholder = '(optional)';
      nInput.addEventListener('change', () => {
        const val = parseInt(nInput.value, 10);
        const highlightedN = Number.isInteger(val) && val > 0 ? val : null;
        controls.setState((s) => ({ ...s, highlightedN }));
      });
      nRow.appendChild(nLabel);
      nRow.appendChild(nInput);
      ctx.panel.appendChild(nRow);

      // Show sigma stack toggle
      const sigmaToggle = createToggleRow({
        labelKey: 'panel.show-sigma-stack',
        i18n: ctx.i18n,
        checked: false,
        onChange: (v) => {
          controls.setState((s) => ({ ...s, showSigmaStack: v }));
        },
      });
      ctx.panel.appendChild(sigmaToggle.element);

      // Show closed-form toggle
      const closedFormToggle = createToggleRow({
        labelKey: 'panel.show-closed-form',
        i18n: ctx.i18n,
        checked: true,
        onChange: (v) => {
          controls.setState((s) => ({ ...s, showClosedForm: v }));
        },
      });
      ctx.panel.appendChild(closedFormToggle.element);

      // Advanced toggle — persists to the SAME storage key as before
      const ADVANCED_KEY = 'algebra.sequences-staircase.advanced';
      const advancedToggle = createToggleRow({
        labelKey: 'btn.advanced',
        i18n: ctx.i18n,
        checked: ctx.advanced,
        onChange: (v) => {
          safeStorage.set(ADVANCED_KEY, v ? 'true' : 'false');
          controls.setState((s) => {
            let nextSpec = s.spec;
            let nextPresetId = s.presetId;
            if (v) {
              // Switching to advanced: load first advanced preset
              const firstAdvanced = SEQUENCE_PRESETS.find((p) => p.advancedOnly);
              if (firstAdvanced) {
                nextPresetId = firstAdvanced.id;
                nextSpec = firstAdvanced.toSpec();
              }
            } else {
              // Reverting to foundation: load first non-advanced preset
              const firstFoundation = SEQUENCE_PRESETS.find((p) => !p.advancedOnly);
              if (firstFoundation) {
                nextPresetId = firstFoundation.id;
                nextSpec = firstFoundation.toSpec();
              }
            }
            return { ...s, advancedMode: v, presetId: nextPresetId, spec: nextSpec };
          });
        },
      });
      ctx.panel.appendChild(advancedToggle.element);

      // ── DOM: Action bar ──
      const actionBar = mountActionBar(ctx.actionBar, ctx.i18n);

      // ── RenderHandle ──
      let lastAdvancedMode: boolean | undefined;
      const update = (state: State): void => {
        const {
          spec: currentSpec,
          highlightedN,
          showSigmaStack,
          showClosedForm,
          advancedMode,
        } = state;

        // --- Sync kind select (add/remove recurrence option as needed) ---
        if (advancedMode && !recurOption) {
          recurOption = document.createElement('option');
          recurOption.value = 'recurrence';
          recurOption.textContent = ctx.i18n.t('class.recurrence');
          kindSelect.appendChild(recurOption);
        } else if (!advancedMode && recurOption) {
          kindSelect.removeChild(recurOption);
          recurOption = null;
        }
        kindSelect.value = currentSpec.kind;

        // --- Sync a₁ slider (guarded: never fight an active drag) ---
        const a1Num = currentSpec.a1.toNumber();
        if (a1Slider.getValue() !== a1Num) a1Slider.setValue(a1Num);

        // --- Sync d/r/c slider label, range, value ---
        const drNum =
          currentSpec.kind === 'arithmetic'
            ? currentSpec.d.toNumber()
            : currentSpec.kind === 'geometric'
              ? currentSpec.r.toNumber()
              : currentSpec.c.toNumber();
        drLabelEl.textContent =
          currentSpec.kind === 'arithmetic' ? 'd:' : currentSpec.kind === 'geometric' ? 'r:' : 'c:';
        const drRange =
          currentSpec.kind === 'geometric' ? { min: -2, max: 2 } : { min: -10, max: 10 };
        drSlider.input.min = String(drRange.min);
        drSlider.input.max = String(drRange.max);
        if (drSlider.getValue() !== drNum) drSlider.setValue(drNum);

        // --- Sync n input ---
        if (highlightedN === null) {
          nInput.value = '';
        } else {
          nInput.value = String(highlightedN);
        }

        // --- Staircase visual ---
        const mode = currentSpec.kind;
        let rdVal: Fraction;
        if (currentSpec.kind === 'arithmetic') {
          rdVal = currentSpec.d;
        } else if (currentSpec.kind === 'geometric') {
          rdVal = currentSpec.r;
        } else {
          rdVal = currentSpec.c;
        }

        const updateObj: Partial<Parameters<typeof staircase.update>[0]> = {
          mode,
          a1: currentSpec.a1,
          ratioOrDiff: rdVal,
          showSigmaStack,
          ...(currentSpec.kind === 'recurrence' ? { offset: currentSpec.d } : {}),
        };
        if (highlightedN !== null) {
          updateObj.highlightStep = highlightedN - 1;
        }
        const domainKey = `${state.presetId}:${currentSpec.kind}`;
        if (domainKey !== lastDomainKey) {
          lastDomainKey = domainKey;
          visibleDomains = fitStaircaseDomains({
            mode,
            a1: currentSpec.a1,
            ratioOrDiff: rdVal,
            ...(currentSpec.kind === 'recurrence' ? { offset: currentSpec.d } : {}),
            steps: 8,
            showSigmaStack,
          });
          viewport.setBase(visibleDomains, { notify: false });
        }
        staircase.update({ ...updateObj, ...visibleDomains });
        legend.setEntries([
          {
            id: 'sequence',
            formula: `aₙ = ${format(closedFormExpr(currentSpec, 'n'), {
              useUnicodeSuperscripts: true,
            })}`,
            color: 'var(--accent)',
          },
        ]);

        // --- Formula display (in-stage) ---
        formulaGroup.replaceChildren();
        if (showClosedForm) {
          const anExpr = closedFormExpr(currentSpec, 'n');
          const anText = createSvgEl('text', { x: 20, y: 24, class: 'alg-text' });
          anText.textContent = `aₙ = ${format(anExpr, { useUnicodeSuperscripts: true })}`;
          formulaGroup.appendChild(anText);

          if (currentSpec.kind === 'arithmetic' || currentSpec.kind === 'geometric') {
            try {
              const snExpr = partialSumClosedForm(currentSpec, 'n');
              const snText = createSvgEl('text', { x: 20, y: 46, class: 'alg-text' });
              snText.textContent = `Sₙ = ${format(snExpr, { useUnicodeSuperscripts: true })}`;
              formulaGroup.appendChild(snText);
            } catch {
              // Recurrence sum not available
            }
          }

          if (highlightedN !== null && highlightedN > 0) {
            const anVal = nthTerm(currentSpec, highlightedN);
            const snVal = partialSum(currentSpec, highlightedN);
            const hlText = createSvgEl('text', {
              x: 780,
              y: 24,
              'text-anchor': 'end',
              class: 'alg-result',
            });
            hlText.textContent = `a_${highlightedN} = ${anVal.toString()},  S_${highlightedN} = ${snVal.toString()}`;
            formulaGroup.appendChild(hlText);
          }
        }

        // --- Action bar ---
        if (advancedMode !== lastAdvancedMode) {
          lastAdvancedMode = advancedMode;
          const availablePresets = advancedMode
            ? SEQUENCE_PRESETS
            : SEQUENCE_PRESETS.filter((p) => !p.advancedOnly);

          actionBar.setActions([
            {
              id: 'reset',
              labelKey: 'btn.reset',
              onClick: () => {
                controls.setState((s) => {
                  const preset = SEQUENCE_PRESETS.find((p) => p.id === s.presetId)!;
                  return { ...s, spec: preset.toSpec(), highlightedN: null };
                });
              },
            },
            {
              id: 'view-reset',
              labelKey: 'btn.reset-view',
              onClick: () => resetView(),
            },
            {
              id: 'randomise',
              labelKey: 'btn.randomise',
              onClick: () => {
                const idx = Math.floor(ctx.prng() * availablePresets.length);
                const preset = availablePresets[idx]!;
                controls.setState((s) => ({
                  ...s,
                  presetId: preset.id,
                  spec: preset.toSpec(),
                  highlightedN: null,
                }));
              },
            },
          ]);
        }
      };

      return {
        update,
        dispose() {
          viewport.dispose();
          staircase.destroy();
          legend.destroy();
          actionBar.clear();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
